import ExcalibrrLogo from '@/components/Work/logos/ExcalibrrLogo';
import auditVideo from '@/assets/cases/excalibrr/1b.mov';
import auditPresentation from '@/assets/cases/excalibrr/1.jpg';
import upgradePlanVideo from '@/assets/cases/excalibrr/2.mov';
import migrationBlockers from '@/assets/cases/excalibrr/3.jpg';
import storybookOverviewVideo from '@/assets/cases/excalibrr/8.mov';
import storybookWelcomeImage from '@/assets/cases/excalibrr/8a.jpg';
import storybookPlaygroundImage from '@/assets/cases/excalibrr/8b.jpg';
import storybookGridImage from '@/assets/cases/excalibrr/8c.jpg';
import storybookChromaticImage from '@/assets/cases/excalibrr/8d.jpg';
import gitDiffSummary from '@/assets/cases/excalibrr/10.jpg';
import dispatchBefore from '@/assets/cases/excalibrr/11a.png';
import dispatchAfter from '@/assets/cases/excalibrr/11b.png';
import driverSchedulesBefore from '@/assets/cases/excalibrr/12a.png';
import driverSchedulesAfter from '@/assets/cases/excalibrr/12b.png';
import driverSchedulesTokens from '@/assets/cases/excalibrr/12c.png';
import scheduleDropdownBefore from '@/assets/cases/excalibrr/13a.png';
import scheduleDropdownAfter from '@/assets/cases/excalibrr/13b.png';
import orderModalBefore from '@/assets/cases/excalibrr/14a.png';
import orderModalAfter from '@/assets/cases/excalibrr/14b.png';
import highlightImage from '@/assets/cases/excalibrr/0.png';
import highlightBlur from '@/assets/cases/excalibrr/0.blur.webp';
import figmaDocumentationVideo from '@/assets/cases/excalibrr/4.mov';
import figmaThemingVideo from '@/assets/cases/excalibrr/5a.mov';
import figmaButtonUsageVideo from '@/assets/cases/excalibrr/5b.mov';
import figmaIconLibraryVideo from '@/assets/cases/excalibrr/5c.mov';
import figmaSlotUsageVideo from '@/assets/cases/excalibrr/7.mov';
import excalibrrAgGridImage from '@/assets/cases/excalibrr/5d.jpg';
import {CaseStudy} from './types';

const excalibrrDesignSystem: CaseStudy = {
    // ============================================
    // FIXED METADATA
    // ============================================
    id: 'excalibrr-design-system',
    slug: 'excalibrr-design-system-upgrade',
    title: 'Excalibrr',
    client: 'Enterprise B2B Client',
    industry: 'Enterprise B2B SaaS',
    projectType: 'Design System Overhaul + Technical Debt Elimination',
    timeline: '8 weeks',
    technologies: [
        'React',
        'TypeScript',
        'Ant Design v5',
        'Figma',
        'Storybook',
        'Less',
        'JavaScript'
    ],

    shortDescription: 'Rebuilt Excalibrr’s entire design system, deleting years of entropy and shipping a themed component library.',
    excerpt: 'Upgraded a legacy design system across 125 pages, eliminated 2 years of technical debt, deleted 7,500 lines of code, and delivered a production-grade Figma design system with Storybook documentation—all in 8 weeks.',
    heroImageUrl: figmaDocumentationVideo,
    logo: ExcalibrrLogo,
    featured: true,
    workHighlight: {
        id: 'excalibrr',
        mediaType: 'image',
        media: highlightImage,
        placeholder: highlightBlur
    },

    // ============================================
    // HERO SECTION DATA
    // ============================================
    heroStatement:
        'Rebuilt Excalibrr’s design system in eight weeks—audited 80+ components, upgraded to Ant Design v5, and shipped a production Figma + Storybook library. Deleted 7,500 lines of entropy while giving designers and engineers one source of truth.',

    heroMetrics: [
        {
            label: 'Components documented',
            value: '174'
        },
        {
            label: 'Lines deleted',
            value: '7,500'
        },
        {
            label: 'Pages upgraded',
            value: '125'
        }
    ],

    heroNarrative: 'We treated Excalibrr as a product transformation, not just an upgrade. That meant auditing every component, rethinking the theming architecture, and building a Figma design system from scratch before touching a single line of code. In parallel we upgraded Ant Design, eliminated lazy-loaded CSS, and created comprehensive Storybook documentation so design and engineering could finally work from the same source of truth.',

    // ============================================
    // SCOPE OF WORK (4 Service Areas)
    // ============================================
    scopeOfWork: [
        {
            icon: 'assessment',
            title: 'Component Audit',
            number: '01',
            description: 'Conducted comprehensive audit of 80+ Excalibrr components across 125 pages, identifying upgrade paths, technical debt, and standardization opportunities.'
        },
        {
            icon: 'palette',
            title: 'Design System Creation',
            number: '02',
            description: 'Built production-grade Figma design system from scratch with componentized library, color tokens, and design documentation for seamless designer-developer collaboration.'
        },
        {
            icon: 'code',
            title: 'Excalibrr Upgrade',
            number: '03',
            description: 'Upgraded Excalibrr to Ant Design v5, eliminated 1,200 lines of lazy-loaded CSS per theme, and replaced with 60-line JavaScript config for instant theming.'
        },
        {
            icon: 'rocket_launch',
            title: 'Product Integration',
            number: '04',
            description: 'Updated PE, OSP, and S&D products for compatibility with upgraded Excalibrr, ensuring production-ready deployment across entire platform.'
        }
    ],

    // ============================================
    // CASE DETAILS (Collapsible Card)
    // ============================================
    caseDetails: {
        task: 'Rework and standardize Excalibrr, the client\'s frontend design system, by upgrading to Ant Design v5 and creating a component-based Figma design system while eliminating years of accumulated technical debt across 125 pages.',

        solution: 'Delivered comprehensive design system transformation—80+ component audit, production-grade Figma library, Ant Design v5 upgrade with 7,500 lines of code deleted, and complete Storybook documentation—while maintaining zero downtime across three production products.',

        result: 'Excalibrr shipped production-ready with unified design system, 60-line theming config (down from 1,200 lines per theme), comprehensive Storybook with 80+ documented components, and Figma library enabling designers to scale rapidly without engineering bottlenecks.',

        stack: [
            'React',
            'TypeScript',
            'Ant Design v5',
            'Figma',
            'Storybook',
            'Less',
            'JavaScript'
        ]
    },

    // ============================================
    // DYNAMIC CONTENT (Visual Storytelling)
    // ============================================
    content: [
        {
            type: 'heading2',
            content: 'Before: design scale was blocked',
            id: 'before',
            description: 'Legacy system missing tokens, documentation, and theming guardrails'
        },
        {
            type: 'paragraph',
            content:
                "Before the engagement, Excalibrr's design system couldn't scale. Designers had no Figma component library, so every new screen was a copy-and-paste job. Engineering relied on sacred knowledge instead of documentation, and 1.2k-1.5k line Less theme files were lazy-loaded inside components, making performance brittle and dark mode unreliable."
        },
        {
            type: 'keyBulletList',
            content: {
                heading: 'Where The System Broke',
                description: 'Design velocity stalled and engineering quality eroded.',
                points: [
                    {
                        icon: 'design_services',
                        title: 'No Figma Design System',
                        text: 'Designers rebuilt pages by hand without reusable components, so patterns drifted immediately after handoff.'
                    },
                    {
                        icon: 'library_books',
                        title: 'No Component Documentation',
                        text: 'Developers copied snippets or taught each other ad hoc, spreading inconsistent prop signatures and behaviors.'
                    },
                    {
                        icon: 'waterfall_chart',
                        title: 'Less Themes Embedded',
                        text: 'Lazy-loaded 1.2k+ line Less overrides lived inside mounted components, crushing performance and breaking dark mode inheritance.'
                    }
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Architecture + Component Audit',
                caption: 'Visualizing how missing foundations and bespoke components caused teams to drift.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: auditVideo,
                                alt: 'Screen recording walking through the component audit',
                                caption: 'Audit walkthrough: prioritizing 174 components with the client engineering leads.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                src: auditPresentation,
                                alt: 'Presentation slide deck used to align audit priorities with the client',
                                caption: 'Client working session deck where the team co-drove the audit priorities.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        {
            type: 'heading2',
            content: 'The Upgrade',
            id: 'upgrade',
            description: 'Ant Design v5 migration and tokenized theming'
        },
        {
            type: 'paragraph',
            content: 'The upgrade started with a breaking-change matrix for Ant Design v5, pairing every legacy component with the new API surface. We rebuilt theming around tokens, removing 1,200-line Less overrides per theme and replacing them with a 60-line configuration layer.'
        },
        {
            type: 'keyBulletList',
            content: {
                heading: 'Migration Playbook',
                description: 'Breaking change coverage without risking production uptime.',
                points: [
                    {
                        icon: 'difference',
                        title: 'Breaking Change Matrix',
                        text: 'Mapped every component to the v5 API, including prop changes, style overrides, and behavioral regressions.'
                    },
                    {
                        icon: 'layers',
                        title: 'Tokenized Architecture',
                        text: 'Replaced Less overrides with Ant Design design tokens tied to a central theme config.'
                    },
                    {
                        icon: 'sync',
                        title: 'Storybook + Figma Harmony',
                        text: 'Locked the component API and visuals in Storybook and Figma before touching product repos, so design and engineering shipped from the same source.'
                    }
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Upgrade Planning',
                caption: 'Sequencing components, tokens, and QA before touching product repos.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: upgradePlanVideo,
                                alt: 'Video walkthrough of the Ant Design upgrade plan',
                                caption: 'Upgrade plan detailing sequencing for 174 components across design and engineering.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: migrationBlockers,
                                alt: 'Slide detailing theming migration and moment.js removal risks',
                                caption: 'Biggest blockers: theming migration and moment.js removal, mapped with client leads for a risk-averse rollout.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        {
            type: 'heading2',
            content: 'Design System + Storybook',
            id: 'design-system',
            description: 'One source of truth for designers and engineers'
        },
        {
            type: 'paragraph',
            content: 'With the code stabilized, we rebuilt the design system in Figma and Storybook simultaneously. Every component ships with tokens, usage guidelines, and slot-based composition so designers can iterate without blocking engineering.'
        },
        {
            type: 'keyBulletList',
            content: {
                heading: 'Unified Library',
                description: 'Figma, tokens, and Storybook stay in lockstep.',
                points: [
                    {
                        icon: 'view_module',
                        title: '174-Component Library',
                        text: 'Documented variants, states, and interaction patterns for the 174 components that drive the platform.'
                    },
                    {
                        icon: 'auto_fix_high',
                        title: 'Slot-Based Patterns',
                        text: 'Advanced Figma slots mirror React composition for effortless UX experiments.'
                    },
                    {
                        icon: 'style',
                        title: 'Theme Playground',
                        text: 'Dark/light themes powered by the same token source across Figma and Storybook.'
                    },
                    {
                        icon: 'video_library',
                        title: 'Interactive Storybook',
                        text: 'Storybook demos include controls, accessibility notes, and copy-ready code snippets.'
                    }
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Figma Design System',
                caption: 'Figma artifacts that synced tokens, variants, and theming before code changes.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: figmaDocumentationVideo,
                                alt: 'Screen recording of the Figma design system documentation and variant usage',
                                caption: 'Documentation walkthrough: modern variant workflows captured for the client team.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: figmaThemingVideo,
                                alt: 'Video showing the themed token system applied across component surfaces in Figma',
                                caption: 'Theme engine: documenting how the tokens cascade through light, dark, and brand variants.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                src: figmaButtonUsageVideo,
                                alt: 'Video showcasing button component variants with icon swaps and states',
                                caption: 'Button variants: state matrix with instant icon swapping for designers.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: figmaSlotUsageVideo,
                                alt: 'Video walkthrough demonstrating slot-driven container components in Figma',
                                caption: 'Slot usage: nested slots let designers remix layouts without new variants.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: figmaIconLibraryVideo,
                                alt: 'Video covering the shared icon library powering the design system',
                                caption: 'Icon library: unified Material + Ant Design set wired into components.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                src: excalibrrAgGridImage,
                                alt: 'Excalibrr AG Grid component system showcasing data-heavy modules',
                                caption: 'AG Grid library: another 100+ data components aligned with the system.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'gallery',
            content: {
                heading: 'Storybook Documentation',
                caption: 'Storybook locked component APIs and usage before repo rollout.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: storybookOverviewVideo,
                                alt: 'Video tour of the Storybook welcome portal and documentation structure',
                                caption: 'Storybook portal: guided walkthrough of the getting started and documentation hub.',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: storybookWelcomeImage,
                                alt: 'Storybook welcome page with getting started guides, architecture overview, and context references',
                                caption: 'Welcome hub: getting started guides, architecture overview, and context playbooks.'
                            },
                            {
                                src: storybookPlaygroundImage,
                                alt: 'Storybook component playground illustrating how props adjust the rendered UI',
                                caption: 'Component playgrounds: see React props reshape components in real time.'
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: storybookGridImage,
                                alt: 'Storybook grid pages detailing advanced data grid functionality',
                                caption: '32-page grid deep dives cover the extended data grid functionality.'
                            },
                            {
                                src: storybookChromaticImage,
                                alt: 'Chromatic UI capturing Storybook regression monitoring for package publishes',
                                caption: 'Chromatic regression suite watches every package publish across components.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        {
            type: 'heading2',
            content: 'Rollout & Impact',
            id: 'rollout',
            description: 'Coordinated launch across 125 pages'
        },
        {
            type: 'paragraph',
            content: 'Rollout was phased with QA and engineering pairing on each product line. We tracked git analytics to ensure the debt stayed gone and used before/after reviews to validate UX consistency.'
        },
        {
            type: 'keyBulletList',
            content: {
                heading: 'Migration Metrics',
                description: 'How we monitored the rollout across PE, OSP, and S&D.',
                points: [
                    {
                        icon: 'commit',
                        title: '1,200 Files Touched',
                        text: 'Repo-wide upgrades merged in tightly scoped batches for easy review.'
                    },
                    {
                        icon: 'delete',
                        title: '7,500 Lines Deleted',
                        text: 'Legacy Less and shadow components removed permanently.'
                    },
                    {
                        icon: 'shield',
                        title: 'Guardrails in Place',
                        text: 'Design tokens linted at build-time to prevent regression to ad-hoc overrides.'
                    }
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Shipping Metrics',
                caption: 'Git analytics from the migration program.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: gitDiffSummary,
                                alt: 'Git diff summary illustrating files changed and deletions during the migration',
                                caption: 'Git diff summary tracking scope, throughput, and deletion totals.'
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Before vs After',
                caption: 'Representative screens illustrating the visual upgrade.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: dispatchBefore,
                                alt: 'Legacy dispatch view before the upgrade',
                                caption: 'Before: dispatch operations buried under dense tables and ad-hoc tags.'
                            },
                            {
                                src: dispatchAfter,
                                alt: 'Upgraded dispatch view after the redesign',
                                caption: 'After: token-driven dispatch board with clarified hierarchy and space.'
                            }
                        ]
                    },
                    {
                        layout: 'triple',
                        images: [
                            {
                                src: driverSchedulesBefore,
                                alt: 'Legacy driver schedules grid before the upgrade',
                                caption: 'Before: driver schedules grid relying on manual color hacks.'
                            },
                            {
                                src: driverSchedulesAfter,
                                alt: 'Driver schedules grid after the redesign',
                                caption: 'After: streamlined driver schedules with consistent tokens and spacing.'
                            },
                            {
                                src: driverSchedulesTokens,
                                alt: 'Driver schedules grid showing color-coded states after the upgrade',
                                caption: 'After: palette system introduces standardized shift states and alerts.'
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: scheduleDropdownAfter,
                                alt: 'Driver schedule dropdown after the redesign',
                                caption: 'After: dropdown aligns with design tokens and accessible state coloring.'
                            },
                            {
                                src: scheduleDropdownBefore,
                                alt: 'Legacy driver schedule dropdown before the upgrade',
                                caption: 'Before: dropdown theming relied on overridden Less with mismatched colors.'
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: orderModalBefore,
                                alt: 'Legacy order details modal before the upgrade',
                                caption: 'Before: order details modal packed with inconsistent spacing and typography.'
                            },
                            {
                                src: orderModalAfter,
                                alt: 'Order details modal after the redesign',
                                caption: 'After: modal applies the new theme, iconography, and layout guardrails.'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        }
    ]
};

export default excalibrrDesignSystem;
